import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../hooks/useApi';
import { menuNavItems, createNavItems, supportNavItem } from './navConfig';
import { apiClient } from '../../services/api';
import LoadingOverlay from '../LoadingOverlay';

export default function LeftSidebar() {
  const { isAuthenticated, user } = useAuth();
  const { user: userData } = useUser();
  const location = useLocation();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if user has an active or canceling subscription
  const hasActiveSubscription = userData?.subscription_status === 'active' || userData?.subscription_status === 'canceling';
  
  // Open Stripe Customer Portal (no email login needed!)
  const openStripePortal = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      console.error('❌ [LeftSidebar] No user ID available');
      alert('Unable to open billing portal. Please try refreshing the page.');
      return;
    }

    if (isLoadingPortal) {
      return; // Prevent multiple clicks
    }

    try {
      setIsLoadingPortal(true);
      
      // Create portal session via API
      const { url } = await apiClient.createPortalSession(
        user.id,
        `${window.location.origin}/my-collection`
      );
      
      // Redirect to Stripe portal (no email needed!)
      window.location.href = url;
    } catch (error) {
      console.error('❌ [LeftSidebar] Failed to open billing portal:', error);
      
      // Close overlay first
      setIsLoadingPortal(false);
      
      // Show error after a brief delay to ensure overlay closes
      setTimeout(() => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.';
        alert(`${errorMessage}\n\nNote: If you're on dev environment, make sure you have a test subscription created with dev's test Stripe keys.`);
      }, 100);
    }
  };

  const SidebarLink = ({ to, icon, label, isExternal, onClick }: { to: string; icon: React.ReactNode; label: string; isExternal?: boolean; onClick?: (e: React.MouseEvent) => void }) => {
    const active = isActive(to);
    const baseClasses = "flex items-center gap-4 px-3 min-h-[56px] transition-all duration-200";
    const activeClasses = active
      ? 'text-white bg-white/10 rounded-2xl'
      : 'text-[#e0e0e0] hover:text-white hover:bg-white/6 rounded-2xl';
    
    if (isExternal || onClick) {
      return (
        <a
          href={onClick ? '#' : to}
          className={`${baseClasses} ${activeClasses}`}
          onClick={onClick}
        >
          <span className="flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center">{icon}</span>
          <span className="text-lg" style={{ fontSize: '18px', fontWeight: 400 }}>{label}</span>
        </a>
      );
    }
    
    return (
      <NavLink
        to={to}
        className={`${baseClasses} ${activeClasses}`}
      >
        <span className="flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center">{icon}</span>
        <span className="text-lg" style={{ fontSize: '18px', fontWeight: 400 }}>{label}</span>
      </NavLink>
    );
  };

  return (
    <div className="hidden lg:flex flex-col w-[280px] h-screen bg-bg border-r border-white/6 fixed left-0 top-0 z-40">
      {/* Top wordmark/logo */}
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-icon.png" alt="GenAnything" className="w-10 h-10" />
          <span className="text-white text-xl" style={{ fontWeight: 400 }}>GenAnything</span>
        </Link>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6">
        <nav className="flex flex-col">
          {/* Main navigation items */}
          <div className="flex flex-col">
            {menuNavItems.map((item) => {
              // If this is the upgrade link and user has active subscription, open portal
              if (item.to === '/upgrade' && hasActiveSubscription) {
                return (
                  <SidebarLink 
                    key={item.to} 
                    to="#" 
                    icon={item.icon} 
                    label={isLoadingPortal ? "Loading..." : "Manage Subscription"}
                    onClick={openStripePortal}
                  />
                );
              }
              return (
                <SidebarLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
              );
            })}
          </div>
          
          {/* Divider */}
          <div className="h-px bg-white/8 my-4" />
          
          {/* Spacing for Create items */}
          <div className="mt-4 mb-3" />
          
          <div className="flex flex-col">
            {createNavItems.map((item) => (
              <SidebarLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
            ))}
          </div>
          
          {/* Divider */}
          <div className="h-px bg-white/8 my-4" />
          
          {/* Support link */}
          <div className="flex flex-col">
            <SidebarLink 
              to={supportNavItem.to}
              icon={supportNavItem.icon}
              label={supportNavItem.label}
              isExternal={true}
            />
          </div>
        </nav>
      </div>

      {/* Bottom footer area - reserved for future icons/actions if needed */}
      <div className="px-6 pb-6 flex-shrink-0 mt-auto">
        {/* Footer area reserved for future icons if needed */}
      </div>

      {/* Loading overlay for portal session */}
      <LoadingOverlay
        show={isLoadingPortal}
        message="Opening Billing Portal..."
      />
    </div>
  );
}
